import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import RefreshIcon from '@mui/icons-material/Refresh'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import LogoutIcon from '@mui/icons-material/Logout'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import SettingsIcon from '@mui/icons-material/Settings'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { UserContext } from '../../App'
import { useWallboardData } from './useWallboardData'
import { buildWallboardTheme } from './wallboardTheme'
import {
  getWallboardThemeOverride,
  setWallboardThemeOverride,
} from './wallboardToken'
import WallboardGrid from './WallboardGrid'

const PAUSE_AFTER_INTERACTION_MS = 60 * 1000
const ROTATION_TICK_MS = 500

const SYNC_CHIP = {
  connecting: { label: 'Connecting…', color: 'default' },
  live: { label: 'Live', color: 'success' },
  delayed: { label: 'Delayed', color: 'warning' },
  offline: { label: 'Offline', color: 'error' },
  blocked: { label: 'Unavailable', color: 'error' },
}

const BLOCKED_MESSAGES = {
  INSUFFICIENT_SUBSCRIPTION_TIER:
    'Wallboards are part of the Premium plan. Ask your administrator about upgrading.',
  TENANT_SUBSCRIPTION_EXPIRED:
    'Your organisation’s subscription has expired — the wallboard will resume automatically once it is renewed.',
  noUserSession: 'Your session has ended. Sign in again to keep the wallboard running.',
}

const FullScreenNotice = ({ title, body }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      textAlign: 'center',
      px: 4,
    }}
  >
    <Typography sx={{ fontSize: '1.6rem', fontWeight: 600 }}>{title}</Typography>
    {body && (
      <Typography sx={{ color: 'text.secondary', maxWidth: 560 }}>{body}</Typography>
    )}
  </Box>
)

// The wallboard display itself — identical for both auth modes:
//  - mode="device": public route, X-Wallboard-Token auth; onExit relocks.
//  - mode="session": logged-in chrome-less route (MainFrame wallboardMode).
const WallboardPage = ({ mode, onExit }) => {
  const { data, loading, syncState, lastUpdated, blocked, refresh } =
    useWallboardData({ mode, onTokenRejected: mode === 'device' ? onExit : undefined })

  const navigate = useNavigate()
  const { user } = useContext(UserContext) ?? {}
  // Settings shortcut: only on the logged-in display, only for site admins
  // (a token device has no user and must stay read-only)
  const canOpenSettings = mode === 'session' && !!user?.security?.isSiteAdmin

  const wallboard = data?.wallboard
  const screens = wallboard?.screens ?? []
  const rotationEnabled = wallboard?.settings?.rotationEnabled !== false

  // Theme: this display's own toggle wins, else the board's configured theme
  const [themeOverride, setThemeOverride] = useState(() =>
    getWallboardThemeOverride(),
  )
  const themeMode = themeOverride ?? wallboard?.settings?.theme ?? 'dark'
  const toggleTheme = () => {
    const next = themeMode === 'dark' ? 'light' : 'dark'
    setWallboardThemeOverride(next)
    setThemeOverride(next)
  }

  const [screenIndex, setScreenIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [paused, setPaused] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [clock, setClock] = useState(new Date())
  const lastInteractionRef = useRef(0)

  // Keep the index valid when the board is edited under a running display
  useEffect(() => {
    if (screenIndex >= screens.length) setScreenIndex(0)
  }, [screens.length, screenIndex])

  // Rotation: deadline-based so a background-throttled tab still advances
  // correctly when it wakes
  useEffect(() => {
    if (!rotationEnabled || screens.length <= 1 || paused) {
      setProgress(0)
      return undefined
    }
    const dwellMs = (screens[screenIndex]?.dwellSeconds ?? 30) * 1000
    const start = Date.now()
    const timer = setInterval(() => {
      const elapsed = Date.now() - start
      setProgress(Math.min((elapsed / dwellMs) * 100, 100))
      if (elapsed >= dwellMs) {
        setScreenIndex((index) => (index + 1) % screens.length)
      }
    }, ROTATION_TICK_MS)
    return () => clearInterval(timer)
  }, [rotationEnabled, screens.length, screenIndex, paused])

  // Any press pauses rotation (someone is reading/pointing); it resumes a
  // minute after the last interaction
  useEffect(() => {
    const onInteraction = () => {
      lastInteractionRef.current = Date.now()
      setPaused(true)
    }
    window.addEventListener('pointerdown', onInteraction)
    window.addEventListener('keydown', onInteraction)
    const resumeTimer = setInterval(() => {
      if (
        lastInteractionRef.current &&
        Date.now() - lastInteractionRef.current > PAUSE_AFTER_INTERACTION_MS
      ) {
        lastInteractionRef.current = 0
        setPaused(false)
      }
    }, 2000)
    return () => {
      window.removeEventListener('pointerdown', onInteraction)
      window.removeEventListener('keydown', onInteraction)
      clearInterval(resumeTimer)
    }
  }, [])

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.()
    } else {
      document.documentElement.requestFullscreen?.()
    }
  }, [])

  const screen = screens[screenIndex]
  const sync = SYNC_CHIP[syncState] ?? SYNC_CHIP.connecting
  const showStaleBanner =
    syncState === 'offline' && lastUpdated && data && !blocked

  return (
    <ThemeProvider theme={buildWallboardTheme(themeMode)}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          color: 'text.primary',
          p: 2,
          gap: 1.5,
          zIndex: 1300,
        }}
      >
        {/* Top bar */}
        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }} noWrap>
              {wallboard?.name ?? 'Wallboard'}
            </Typography>
            {screen?.name && screens.length > 1 && (
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }} noWrap>
                {screen.name}
              </Typography>
            )}
          </Box>

          {/* Screen dots */}
          {screens.length > 1 && (
            <Stack direction="row" gap={0.75} sx={{ ml: 1 }}>
              {screens.map((candidate, index) => (
                <Box
                  key={candidate._id ?? index}
                  onClick={() => setScreenIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    bgcolor:
                      index === screenIndex ? 'primary.main' : 'rgba(148,163,184,0.3)',
                  }}
                />
              ))}
            </Stack>
          )}
          {paused && (
            <Tooltip title="Rotation paused — resumes a minute after the last interaction">
              <PauseCircleOutlineIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Chip size="small" label={sync.label} color={sync.color} variant="outlined" />
          {lastUpdated && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              Updated {moment(lastUpdated).format('HH:mm:ss')}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={refresh} sx={{ color: 'text.secondary' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton size="small" onClick={toggleTheme} sx={{ color: 'text.secondary' }}>
              {themeMode === 'dark' ? (
                <LightModeIcon fontSize="small" />
              ) : (
                <DarkModeIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton size="small" onClick={toggleFullscreen} sx={{ color: 'text.secondary' }}>
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {canOpenSettings && (
            <Tooltip title="Wallboard settings">
              <IconButton
                size="small"
                onClick={() => navigate('/wallboard-settings')}
                sx={{ color: 'text.secondary' }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onExit && (
            <Tooltip title={mode === 'device' ? 'Lock this display' : 'Exit wallboard'}>
              <IconButton size="small" onClick={onExit} sx={{ color: 'text.secondary' }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {wallboard?.settings?.showClock !== false && (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.5rem',
                fontVariantNumeric: 'tabular-nums',
                ml: 1,
              }}
            >
              {moment(clock).format('HH:mm')}
            </Typography>
          )}
        </Stack>

        {/* Dwell progress */}
        {rotationEnabled && screens.length > 1 && (
          <LinearProgress
            variant="determinate"
            value={paused ? 0 : progress}
            sx={{
              height: 3,
              borderRadius: 3,
              bgcolor: 'rgba(148,163,184,0.12)',
            }}
          />
        )}

        {showStaleBanner && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
            Connection lost — showing data from{' '}
            {moment(lastUpdated).format('HH:mm:ss')}. Retrying automatically.
          </Alert>
        )}

        {/* Body */}
        {blocked ? (
          <FullScreenNotice
            title="Wallboard unavailable"
            body={
              BLOCKED_MESSAGES[blocked.errorCode] ??
              'The wallboard can’t reach AmbuNet right now. It will keep retrying automatically.'
            }
          />
        ) : loading && !data ? (
          <FullScreenNotice title="Loading wallboard…" />
        ) : !wallboard ? (
          <FullScreenNotice
            title="No wallboard configured yet"
            body="A site admin can build one under Wallboard Settings — add screens and widgets and this display will pick them up within a minute."
          />
        ) : screens.length === 0 ? (
          <FullScreenNotice
            title="This wallboard has no screens yet"
            body="Add a screen and some widgets in Wallboard Settings."
          />
        ) : (
          <WallboardGrid screen={screen} data={data?.data} />
        )}
      </Box>
    </ThemeProvider>
  )
}

export default WallboardPage
